// A goldsmith: an open-fronted forge workshop with a glowing hearth, an anvil
// and a stack of minted gold coins out front. Ported 1:1 from age-of-mcp's
// goldsmith.ts: a local walls() generator plus the shared foundationSlab/scaffold
// helpers.
import * as THREE from "three";
import { defineModel, benchWork, foundationSlab, scaffold, smokePlume, firePulse, EFFECT_LOOP, type Part } from "../../lib";

const WALL = "#b59a6a";
const ROOF = "#6a4a2a";
const STONE = "#6a6a6a";
const FIRE = "#ff9a3c";
const GOLD = "#e8c24a";
const ANVIL = "#3a3a40";

function walls(h: number): Part {
  return { geometry: new THREE.BoxGeometry(0.74, h, 0.74), color: WALL, position: [0, 0.14 + h / 2, 0] };
}

// The "forge is hot" cue: the hearth glow pulses while a thin smoke plume rises
// from a stone flue stub poking through the roof above the hearth.
const plume = smokePlume([-0.28, 1.08, -0.22], { rise: 0.3, radius: 0.045 });

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) return [foundationSlab(0.84), walls(0.3), ...scaffold(0.82, 0.6)];
  if (s === 2) return [foundationSlab(0.84), walls(0.58), ...scaffold(0.82, 1.0)];

  const wallH = 0.6, top = 0.14 + wallH, rise = 0.28, halfD = 0.44;
  const slope = Math.hypot(halfD, rise), ang = Math.atan2(rise, halfD);
  return [
    foundationSlab(0.84),
    walls(wallH),
    { geometry: new THREE.BoxGeometry(0.9, 0.06, slope), color: ROOF, position: [0, top + rise / 2, halfD / 2], rotation: [ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.9, 0.06, slope), color: ROOF, position: [0, top + rise / 2, -halfD / 2], rotation: [-ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.22, 0.34, 0.22), color: STONE, position: [-0.28, 0.14 + 0.17, -0.22] },
    { geometry: new THREE.BoxGeometry(0.14, 0.12, 0.06), color: FIRE, position: [-0.28, 0.14 + 0.16, -0.08], name: "forge-fire" },
    // The flue stub over the hearth, poking through the back roof slope.
    { geometry: new THREE.BoxGeometry(0.1, 0.22, 0.1), color: STONE, position: [-0.28, 0.97, -0.22] },
    ...plume.parts,
    { geometry: new THREE.BoxGeometry(0.16, 0.06, 0.1), color: ANVIL, position: [0.18, 0.14 + 0.11, 0.34] },
    { geometry: new THREE.BoxGeometry(0.08, 0.1, 0.08), color: ANVIL, position: [0.18, 0.14 + 0.05, 0.34] },
    { geometry: new THREE.CylinderGeometry(0.07, 0.07, 0.04, 10), color: GOLD, position: [-0.18, 0.14 + 0.02, 0.4] },
    { geometry: new THREE.CylinderGeometry(0.07, 0.07, 0.04, 10), color: GOLD, position: [-0.18, 0.14 + 0.06, 0.4] },
    { geometry: new THREE.CylinderGeometry(0.07, 0.07, 0.04, 10), color: GOLD, position: [-0.1, 0.14 + 0.02, 0.36] },
  ];
}

export const goldsmith = defineModel({
  id: "goldsmith",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Goldsmith",
  tags: ["building","goldsmith","low-poly","age-of-mcp","classic","animated"],
  summary: "Low-poly goldsmith.",
  description: "A goldsmith's forge that refines raw gold into minted coins.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Forge hot",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [...plume.tracks, firePulse("forge-fire", [-0.28, 0.14 + 0.16, -0.08], { amount: 1.35 })],
    },
  ],
  stations: [
    { id: "bench", position: [0.18, 0, 0.56], facing: Math.PI, description: "The goldsmith's bench." },
  ],
  interactions: [
    { id: "work", label: "At the bench", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
